import { ChevronRightThinner } from '@Components/Common/Icons/Icon';
import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import OwnableTheme from '@Theme';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import type { GroupTypeBase, Styles } from 'react-select';
import Select from 'react-select';

import { useSelectPresenter } from './SelectPresenter';

const selectStyle: Partial<Styles<IOption, false, GroupTypeBase<IOption>>> = {
  container: (provided) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: OwnableTheme.colors.black,
  }),
  valueContainer: () => ({
    display: 'flex',
    paddingLeft: '10px',
    flex: 1,
  }),
  indicatorSeparator: () => ({
    width: 0,
  }),
  indicatorsContainer: () => ({
    transform: 'rotateZ(-90deg)',
  }),
  control: () => ({
    width: '100%',
    border: `0.6px solid ${OwnableTheme.colors.darkGray}`,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    cursor: 'pointer',
    height: IS_SMALL_SCREEN ? 27 : 40,
  }),
  menu: (provided) => ({
    ...provided,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '400px',
  }),
  option: (_provided, state) => ({
    color:
      state.isSelected || state.isFocused
        ? OwnableTheme.colors.black
        : OwnableTheme.colors.darkGray,
    cursor: 'pointer',
    backgroundColor: state.isFocused ? '#f2f2f2' : OwnableTheme.colors.white,
    letterSpacing: '3%',
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    fontSize: `${IS_SMALL_SCREEN ? 10 : 15}px`,
    padding: `${IS_SMALL_SCREEN ? '8px 9px' : '11px 14px'}`,
  }),
  singleValue: () => ({
    color: OwnableTheme.colors.black,
    fontSize: `${IS_SMALL_SCREEN ? 10 : 15}px`,
    letterSpacing: '3%',
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: OwnableTheme.colors.black,
  }),
  loadingIndicator: (provided) => ({
    ...provided,
    color: OwnableTheme.colors.black,
  }),
  input: (provided) => ({
    ...provided,
    fontSize: `${IS_SMALL_SCREEN ? 10 : 15}px`,
    fontFamily: OwnableTheme.fonts.gilroy.regular,
    letterSpacing: '3%',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: `${IS_SMALL_SCREEN ? 10 : 15}px`,
    letterSpacing: '3%',
    color: OwnableTheme.colors.black,
    fontFamily: OwnableTheme.fonts.gilroy.regular,
  }),
};

type IOption = {
  label: string;
  value: number;
};

type CustomSelectProps = {
  options: IOption[];
  selectedOption?: IOption | undefined;
  placeholder?: string;
  overrideStyles?: Partial<Styles<IOption, false, GroupTypeBase<IOption>>>;
  onChange?: (value: IOption | null) => void;
};

const CustomSelect: FC<CustomSelectProps> = ({
  options,
  selectedOption,
  placeholder,
  overrideStyles,
  onChange,
}) => {
  const { selectOpened } = useSelectPresenter();
  const style = overrideStyles ?? selectStyle;
  // noinspection JSUnusedGlobalSymbols
  return (
    <Select
      options={options}
      styles={{
        ...style,
        indicatorsContainer: () => ({
          transform: `rotateZ(${selectOpened.value ? '90' : '0'}deg)`,
          transition: '0.3s',
          marginRight: 12,
        }),
      }}
      defaultValue={selectedOption}
      placeholder={placeholder}
      onChange={onChange}
      menuPortalTarget={document.getElementById('root') ?? undefined}
      onMenuOpen={() => {
        selectOpened.set(true);
      }}
      onMenuClose={() => {
        selectOpened.set(false);
      }}
      components={{
        DropdownIndicator: () => <ChevronRightThinner width={7} height={11} />,
      }}
    />
  );
};

export default observer(CustomSelect);
